"use client";

import { useId } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { CMSImage } from "@/components/frontend/ui/image";
import type { LogoSliderBlock } from "@/payload-types";

interface LogoSliderCarouselProps {
	slides: NonNullable<NonNullable<LogoSliderBlock["slides"]>["items"]>[number][];
	slidesAmount?: number;
}

const navButtonClass = cn(
	"absolute top-1/2 z-10 grid size-9 translate-y-[calc(-50%-20px)] place-items-center rounded-full enabled:cursor-pointer",
	"border border-black/10 bg-white shadow-sm transition disabled:opacity-40 enabled:hover:opacity-60",
);

export const LogoSliderCarousel: React.FC<LogoSliderCarouselProps> = ({ slides, slidesAmount = 3 }) => {
	const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
	const prevClass = `logo-slider-prev-${uid}`;
	const nextClass = `logo-slider-next-${uid}`;

	if (slides.length === 0) return null;

	return (
		<div className="relative w-full">
			<Swiper
				modules={[Navigation, Pagination]}
				className="overflow-hidden!"
				navigation={{ prevEl: `.${prevClass}`, nextEl: `.${nextClass}` }}
				pagination={{ clickable: true }}
				style={{ "--swiper-pagination-color": "var(--ms-color-primary)" } as React.CSSProperties}
				spaceBetween={16}
				slidesPerView={slidesAmount}
			>
				{slides.map((slide, index) => (
					<SwiperSlide key={slide.id ?? index} className="flex! items-center justify-center">
						{slide.logo &&
							(slide.url ? (
								<a
									href={slide.url}
									target="_blank"
									rel="noopener noreferrer"
									className="flex items-center justify-center hover:opacity-70 transition-opacity"
								>
									<CMSImage media={slide.logo} className="h-16! w-auto object-contain" />
								</a>
							) : (
								<CMSImage media={slide.logo} className="h-16! w-auto object-contain" />
							))}
					</SwiperSlide>
				))}
			</Swiper>

			<button type="button" aria-label="Previous slide" className={cn(prevClass, "left-3", navButtonClass)}>
				<ChevronLeft className="size-4" />
			</button>
			<button type="button" aria-label="Next slide" className={cn(nextClass, "right-3", navButtonClass)}>
				<ChevronRight className="size-4" />
			</button>
		</div>
	);
};
